import {FormInstance} from 'antd';

import {AnalyticsTarget, track} from '../../../../../service/analytics/analytics';
import {getEnumValue, getEnumValueEnsure} from '../../../../../util/enum';
import {QueryMapType} from '../../../../../util/type';

import {
    AutoAbuseEnum,
    ContentOfReviewsEnum,
    DeletedReviewsEnum,
    HasNotesEnum,
    HiddenReviewsEnum,
    ModeratedEnum,
    PossibilityOfAnAnswerEnum,
    RepliesToReviewsEnum,
    ReplyPublishedEnum,
    ReviewChangedEnum,
    ReviewNameEnum,
    ReviewsFilterDataType,
    ReviewsFilterFieldNameEnum,
    ReviewsTonalityFilterEnum,
    WaitingForSecondAnswerEnum,
    WithoutTagEnum,
} from './reviews-filter-type';

export function applyReviewFilterDateFieldsToForm(form: FormInstance, filterState: ReviewsFilterDataType): void {
    form.setFieldsValue({
        [ReviewsFilterFieldNameEnum.dateFrom]: filterState['date-from'],
        [ReviewsFilterFieldNameEnum.dateTo]: filterState['date-to'],
        [ReviewsFilterFieldNameEnum.collectionDateFrom]: filterState['collection-date-from'],
        [ReviewsFilterFieldNameEnum.collectionDateTo]: filterState['collection-date-to'],
        [ReviewsFilterFieldNameEnum.replyDateFrom]: filterState['reply-date-from'],
        [ReviewsFilterFieldNameEnum.replyDateTo]: filterState['reply-date-to'],
    });
}

export function trackAnalytics(filterName: string, value: Array<string | number> | string): void {
    track(
        `${AnalyticsTarget.ReviewsManagement.Reviews.UseFilter} ${filterName.replaceAll(/[_-]/g, ' ')}`,
        Array.isArray(value) ? value.join(', ') : value
    );
}

function getDateFromFormValue(value: unknown): Date | null {
    if (value instanceof Date) {
        return value;
    }

    if (value && typeof (value as {toDate?: unknown}).toDate === 'function') {
        return (value as {toDate: () => Date}).toDate();
    }

    return null;
}

function getDateFromUrlValue(value: string | void): Date | null {
    if (!value) {
        return null;
    }

    const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;

    if (dateOnlyPattern.test(value)) {
        const [yearRaw, monthRaw, dayRaw] = value.split('-');

        return new Date(Number(yearRaw), Number(monthRaw) - 1, Number(dayRaw));
    }

    return new Date(value);
}

// eslint-disable-next-line complexity, max-statements,sonarjs/cognitive-complexity
export function getReviewsFilterDataFromForm(form: {
    getFieldValue: (fieldName: string) => unknown;
}): ReviewsFilterDataType {
    const reviewSourceListRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.sourceList);
    const sourceList: Array<number> = Array.isArray(reviewSourceListRaw) ? reviewSourceListRaw.map(Number) : [];
    const reviewStatusListRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.processStatus);
    const statusList: Array<string> = Array.isArray(reviewStatusListRaw) ? reviewStatusListRaw : [];
    const customReviewStatusListRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.customProcessStatus);
    const customStatusList: Array<string> = Array.isArray(customReviewStatusListRaw) ? customReviewStatusListRaw : [];
    const ratingListRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.ratingList);
    const ratingList: Array<number> = Array.isArray(ratingListRaw) ? ratingListRaw.map(Number) : [];
    const employeeResponsibleRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.employeesResponsible);
    const employeeResponsible: Array<string> = Array.isArray(employeeResponsibleRaw)
        ? employeeResponsibleRaw.filter(Boolean)
        : [];
    const agencyRepliersRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.agencyRepliers);
    const agencyRepliers: Array<string> = Array.isArray(agencyRepliersRaw) ? agencyRepliersRaw.filter(Boolean) : [];
    const employeeRespondingRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.employeesResponding);
    const employeeResponding: Array<string> = Array.isArray(employeeRespondingRaw)
        ? employeeRespondingRaw.filter(Boolean)
        : [];
    const doctorsRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.doctorNames);
    const doctors: Array<string> = Array.isArray(doctorsRaw) ? doctorsRaw.map(String) : [];
    const tagListRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.tagList);
    const tagList: Array<number> = Array.isArray(tagListRaw)
        ? tagListRaw.flatMap((item) => (typeof item === 'string' ? item.split(', ').map(Number) : item))
        : [];
    const autoReplayRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.autoReplay);
    const autoReplay: Array<string> = Array.isArray(autoReplayRaw) ? autoReplayRaw.map(String) : [];
    const tonalityRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.sentiments);
    const tonality: Array<ReviewsTonalityFilterEnum> = Array.isArray(tonalityRaw)
        ? tonalityRaw
              .map((value) => getEnumValue<ReviewsTonalityFilterEnum>(ReviewsTonalityFilterEnum, value))
              .filter((value): value is ReviewsTonalityFilterEnum => value !== null)
        : [];

    const withoutTag: WithoutTagEnum = getEnumValueEnsure<WithoutTagEnum>(
        WithoutTagEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.withoutTag),
        WithoutTagEnum.noSelected
    );
    const hasNotes: HasNotesEnum = getEnumValueEnsure<HasNotesEnum>(
        HasNotesEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.hasNotes),
        HasNotesEnum.noSelected
    );
    const hiddenReviews: HiddenReviewsEnum = getEnumValueEnsure<HiddenReviewsEnum>(
        HiddenReviewsEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.hiddenReviews),
        HiddenReviewsEnum.noSelected
    );

    const contentOfReviews: ContentOfReviewsEnum = getEnumValueEnsure<ContentOfReviewsEnum>(
        ContentOfReviewsEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.contentOfReviews),
        ContentOfReviewsEnum.noSelected
    );
    const deletedReviews: DeletedReviewsEnum = getEnumValueEnsure<DeletedReviewsEnum>(
        DeletedReviewsEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.deletedReviews),
        DeletedReviewsEnum.noSelected
    );
    const repliesToReviews: RepliesToReviewsEnum = getEnumValueEnsure<RepliesToReviewsEnum>(
        RepliesToReviewsEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.repliesToReviews),
        RepliesToReviewsEnum.noSelected
    );
    const possibilityOfAnAnswer: PossibilityOfAnAnswerEnum = getEnumValueEnsure<PossibilityOfAnAnswerEnum>(
        PossibilityOfAnAnswerEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.possibilityOfAnAnswer),
        PossibilityOfAnAnswerEnum.noSelected
    );
    const reviewType: ReviewNameEnum = getEnumValueEnsure<ReviewNameEnum>(
        ReviewNameEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.reviewType),
        ReviewNameEnum.noSelected
    );
    const abuseStatusRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.abuseStatus);
    const abuseStatus: Array<string> = Array.isArray(abuseStatusRaw) ? abuseStatusRaw.map(String) : [];
    const autoAbuseRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.autoAbuse);
    const autoAbuse: AutoAbuseEnum = getEnumValueEnsure<AutoAbuseEnum>(
        AutoAbuseEnum,
        autoAbuseRaw,
        AutoAbuseEnum.notSelected
    );
    const reviewChanged: ReviewChangedEnum = getEnumValueEnsure<ReviewChangedEnum>(
        ReviewChangedEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.reviewChanged),
        ReviewChangedEnum.noSelected
    );
    const waitingForSecondAnswer: WaitingForSecondAnswerEnum = getEnumValueEnsure<WaitingForSecondAnswerEnum>(
        WaitingForSecondAnswerEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.waitingForSecondAnswer),
        WaitingForSecondAnswerEnum.noSelected
    );
    const reviewModerated: ModeratedEnum = getEnumValueEnsure<ModeratedEnum>(
        ModeratedEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.reviewModerated),
        ModeratedEnum.noSelected
    );
    const replyPublished: ReplyPublishedEnum = getEnumValueEnsure<ReplyPublishedEnum>(
        ReplyPublishedEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.replyPublished),
        ReplyPublishedEnum.noSelected
    );

    const dateFromRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.dateFrom);
    const dateToRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.dateTo);
    const dateFrom = getDateFromFormValue(dateFromRaw);
    const dateTo = getDateFromFormValue(dateToRaw);

    const collectionDateFromRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.collectionDateFrom);
    const collectionDateToRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.collectionDateTo);
    const collectionDateFrom = getDateFromFormValue(collectionDateFromRaw);
    const collectionDateTo = getDateFromFormValue(collectionDateToRaw);

    const replyDateFromRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.replyDateFrom);
    const replyDateToRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.replyDateTo);
    const replyDateFrom = getDateFromFormValue(replyDateFromRaw);
    const replyDateTo = getDateFromFormValue(replyDateToRaw);

    const brandIdsRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.brandIds);
    const brandIds: number | null = brandIdsRaw ? Number(brandIdsRaw) : null;

    return {
        [ReviewsFilterFieldNameEnum.brandIds]: brandIds,
        [ReviewsFilterFieldNameEnum.sourceList]: sourceList,
        [ReviewsFilterFieldNameEnum.processStatus]: statusList,
        [ReviewsFilterFieldNameEnum.customProcessStatus]: customStatusList,
        [ReviewsFilterFieldNameEnum.ratingList]: ratingList,
        [ReviewsFilterFieldNameEnum.employeesResponding]: employeeResponding,
        [ReviewsFilterFieldNameEnum.employeesResponsible]: employeeResponsible,
        [ReviewsFilterFieldNameEnum.doctorNames]: doctors,
        [ReviewsFilterFieldNameEnum.tagList]: tagList,
        [ReviewsFilterFieldNameEnum.withoutTag]: withoutTag,
        [ReviewsFilterFieldNameEnum.hasNotes]: hasNotes,
        [ReviewsFilterFieldNameEnum.hiddenReviews]: hiddenReviews,

        [ReviewsFilterFieldNameEnum.autoReplay]: autoReplay,
        [ReviewsFilterFieldNameEnum.contentOfReviews]: contentOfReviews,
        [ReviewsFilterFieldNameEnum.deletedReviews]: deletedReviews,
        [ReviewsFilterFieldNameEnum.repliesToReviews]: repliesToReviews,
        [ReviewsFilterFieldNameEnum.possibilityOfAnAnswer]: possibilityOfAnAnswer,
        [ReviewsFilterFieldNameEnum.reviewType]: reviewType,
        [ReviewsFilterFieldNameEnum.abuseStatus]: abuseStatus,
        [ReviewsFilterFieldNameEnum.autoAbuse]: autoAbuse,
        [ReviewsFilterFieldNameEnum.reviewChanged]: reviewChanged,
        [ReviewsFilterFieldNameEnum.waitingForSecondAnswer]: waitingForSecondAnswer,
        [ReviewsFilterFieldNameEnum.reviewModerated]: reviewModerated,
        [ReviewsFilterFieldNameEnum.replyPublished]: replyPublished,
        [ReviewsFilterFieldNameEnum.sentiments]: tonality,

        [ReviewsFilterFieldNameEnum.dateFrom]: dateFrom,
        [ReviewsFilterFieldNameEnum.dateTo]: dateTo,
        [ReviewsFilterFieldNameEnum.collectionDateFrom]: collectionDateFrom,
        [ReviewsFilterFieldNameEnum.collectionDateTo]: collectionDateTo,
        [ReviewsFilterFieldNameEnum.replyDateFrom]: replyDateFrom,
        [ReviewsFilterFieldNameEnum.replyDateTo]: replyDateTo,
        [ReviewsFilterFieldNameEnum.agencyRepliers]: agencyRepliers,
    };
}

// eslint-disable-next-line max-statements, complexity, sonarjs/cognitive-complexity
export function getReviewsFilterDataFromUrlQuery(urlQueries: QueryMapType): ReviewsFilterDataType {
    const reviewSourceListRaw = urlQueries[ReviewsFilterFieldNameEnum.sourceList];
    const sourceList: Array<number> =
        typeof reviewSourceListRaw === 'string' ? reviewSourceListRaw.split(',').map(Number) : [];
    const reviewStatusListRaw = urlQueries[ReviewsFilterFieldNameEnum.processStatus];
    const statusList: Array<string> = typeof reviewStatusListRaw === 'string' ? reviewStatusListRaw.split(',') : [];
    const customReviewStatusListRaw = urlQueries[ReviewsFilterFieldNameEnum.customProcessStatus];
    const customStatusList: Array<string> =
        typeof customReviewStatusListRaw === 'string' ? customReviewStatusListRaw.split(',') : [];
    const ratingListRaw = urlQueries[ReviewsFilterFieldNameEnum.ratingList];
    const ratingList: Array<number> = typeof ratingListRaw === 'string' ? ratingListRaw.split(',').map(Number) : [];
    const employeeResponsibleRaw = urlQueries[ReviewsFilterFieldNameEnum.employeesResponsible];
    const employeeResponsible: Array<string> = employeeResponsibleRaw ? employeeResponsibleRaw.split(',') : [];
    const agencyRepliersRaw = urlQueries[ReviewsFilterFieldNameEnum.agencyRepliers];
    const agencyRepliers: Array<string> = agencyRepliersRaw ? agencyRepliersRaw.split(',') : [];
    const employeeRespondingRaw = urlQueries[ReviewsFilterFieldNameEnum.employeesResponding];
    const employeeResponding: Array<string> = employeeRespondingRaw ? employeeRespondingRaw.split(',') : [];
    const doctorsRaw = urlQueries[ReviewsFilterFieldNameEnum.doctorNames];
    const doctors: Array<string> = doctorsRaw ? doctorsRaw.split(',') : [];
    const tagListRaw = urlQueries[ReviewsFilterFieldNameEnum.tagList];
    const tagList: Array<number> = typeof tagListRaw === 'string' ? tagListRaw.split(',').map(Number) : [];
    const autoReplayRaw = urlQueries[ReviewsFilterFieldNameEnum.autoReplay];
    const autoReplay: Array<string> = autoReplayRaw ? autoReplayRaw.split(',') : [];
    const tonality: Array<ReviewsTonalityFilterEnum> =
        urlQueries[ReviewsFilterFieldNameEnum.sentiments]
            ?.split(',')
            .map((value) => getEnumValue<ReviewsTonalityFilterEnum>(ReviewsTonalityFilterEnum, value))
            .filter((value): value is ReviewsTonalityFilterEnum => value !== null) || [];

    const withoutTag: WithoutTagEnum = getEnumValueEnsure<WithoutTagEnum>(
        WithoutTagEnum,
        urlQueries[ReviewsFilterFieldNameEnum.withoutTag],
        WithoutTagEnum.noSelected
    );
    const hasNotes: HasNotesEnum = getEnumValueEnsure<HasNotesEnum>(
        HasNotesEnum,
        urlQueries[ReviewsFilterFieldNameEnum.hasNotes],
        HasNotesEnum.noSelected
    );
    const hiddenReviews: HiddenReviewsEnum = getEnumValueEnsure<HiddenReviewsEnum>(
        HiddenReviewsEnum,
        urlQueries[ReviewsFilterFieldNameEnum.hiddenReviews],
        HiddenReviewsEnum.noSelected
    );

    const contentOfReviews: ContentOfReviewsEnum = getEnumValueEnsure<ContentOfReviewsEnum>(
        ContentOfReviewsEnum,
        urlQueries[ReviewsFilterFieldNameEnum.contentOfReviews],
        ContentOfReviewsEnum.noSelected
    );
    const deletedReviews: DeletedReviewsEnum = getEnumValueEnsure<DeletedReviewsEnum>(
        DeletedReviewsEnum,
        urlQueries[ReviewsFilterFieldNameEnum.deletedReviews],
        DeletedReviewsEnum.noSelected
    );
    const repliesToReviews: RepliesToReviewsEnum = getEnumValueEnsure<RepliesToReviewsEnum>(
        RepliesToReviewsEnum,
        urlQueries[ReviewsFilterFieldNameEnum.repliesToReviews],
        RepliesToReviewsEnum.noSelected
    );
    const possibilityOfAnAnswer: PossibilityOfAnAnswerEnum = getEnumValueEnsure<PossibilityOfAnAnswerEnum>(
        PossibilityOfAnAnswerEnum,
        urlQueries[ReviewsFilterFieldNameEnum.possibilityOfAnAnswer],
        PossibilityOfAnAnswerEnum.noSelected
    );
    const reviewType: ReviewNameEnum = getEnumValueEnsure<ReviewNameEnum>(
        ReviewNameEnum,
        urlQueries[ReviewsFilterFieldNameEnum.reviewType],
        ReviewNameEnum.noSelected
    );
    const abuseStatusRaw = urlQueries[ReviewsFilterFieldNameEnum.abuseStatus];
    const abuseStatus: Array<string> = typeof abuseStatusRaw === 'string' ? abuseStatusRaw.split(',') : [];
    const autoAbuseRaw = urlQueries[ReviewsFilterFieldNameEnum.autoAbuse];
    const autoAbuse: AutoAbuseEnum = getEnumValueEnsure<AutoAbuseEnum>(
        AutoAbuseEnum,
        autoAbuseRaw,
        AutoAbuseEnum.notSelected
    );
    const reviewChanged: ReviewChangedEnum = getEnumValueEnsure<ReviewChangedEnum>(
        ReviewChangedEnum,
        urlQueries[ReviewsFilterFieldNameEnum.reviewChanged],
        ReviewChangedEnum.noSelected
    );
    const waitingForSecondAnswer: WaitingForSecondAnswerEnum = getEnumValueEnsure<WaitingForSecondAnswerEnum>(
        WaitingForSecondAnswerEnum,
        urlQueries[ReviewsFilterFieldNameEnum.waitingForSecondAnswer],
        WaitingForSecondAnswerEnum.noSelected
    );
    const reviewModerated: ModeratedEnum = getEnumValueEnsure<ModeratedEnum>(
        ModeratedEnum,
        urlQueries[ReviewsFilterFieldNameEnum.reviewModerated],
        ModeratedEnum.noSelected
    );
    const replyPublished: ReplyPublishedEnum = getEnumValueEnsure<ReplyPublishedEnum>(
        ReplyPublishedEnum,
        urlQueries[ReviewsFilterFieldNameEnum.replyPublished],
        ReplyPublishedEnum.noSelected
    );

    const dateFromRaw = urlQueries[ReviewsFilterFieldNameEnum.dateFrom];
    const dateFrom = getDateFromUrlValue(dateFromRaw);
    const dateToRaw = urlQueries[ReviewsFilterFieldNameEnum.dateTo];
    const dateTo = getDateFromUrlValue(dateToRaw);

    const collectionDateFromRaw = urlQueries[ReviewsFilterFieldNameEnum.collectionDateFrom];
    const collectionDateFrom = getDateFromUrlValue(collectionDateFromRaw);
    const collectionDateToRaw = urlQueries[ReviewsFilterFieldNameEnum.collectionDateTo];
    const collectionDateTo = getDateFromUrlValue(collectionDateToRaw);

    const replyDateFromRaw = urlQueries[ReviewsFilterFieldNameEnum.replyDateFrom];
    const replyDateFrom = getDateFromUrlValue(replyDateFromRaw);
    const replyDateToRaw = urlQueries[ReviewsFilterFieldNameEnum.replyDateTo];
    const replyDateTo = getDateFromUrlValue(replyDateToRaw);

    const brandIdsRaw = urlQueries[ReviewsFilterFieldNameEnum.brandIds];
    const brandIds: number | null = typeof brandIdsRaw === 'string' ? Number(brandIdsRaw) : null;

    return {
        [ReviewsFilterFieldNameEnum.brandIds]: brandIds,
        [ReviewsFilterFieldNameEnum.sourceList]: sourceList,
        [ReviewsFilterFieldNameEnum.processStatus]: statusList,
        [ReviewsFilterFieldNameEnum.customProcessStatus]: customStatusList,
        [ReviewsFilterFieldNameEnum.ratingList]: ratingList,
        [ReviewsFilterFieldNameEnum.employeesResponsible]: employeeResponsible,
        [ReviewsFilterFieldNameEnum.employeesResponding]: employeeResponding,
        [ReviewsFilterFieldNameEnum.doctorNames]: doctors,
        [ReviewsFilterFieldNameEnum.tagList]: tagList,
        [ReviewsFilterFieldNameEnum.withoutTag]: withoutTag,
        [ReviewsFilterFieldNameEnum.hasNotes]: hasNotes,
        [ReviewsFilterFieldNameEnum.hiddenReviews]: hiddenReviews,

        [ReviewsFilterFieldNameEnum.autoReplay]: autoReplay,
        [ReviewsFilterFieldNameEnum.contentOfReviews]: contentOfReviews,
        [ReviewsFilterFieldNameEnum.deletedReviews]: deletedReviews,
        [ReviewsFilterFieldNameEnum.repliesToReviews]: repliesToReviews,
        [ReviewsFilterFieldNameEnum.possibilityOfAnAnswer]: possibilityOfAnAnswer,
        [ReviewsFilterFieldNameEnum.reviewType]: reviewType,
        [ReviewsFilterFieldNameEnum.abuseStatus]: abuseStatus,
        [ReviewsFilterFieldNameEnum.autoAbuse]: autoAbuse,
        [ReviewsFilterFieldNameEnum.reviewChanged]: reviewChanged,
        [ReviewsFilterFieldNameEnum.waitingForSecondAnswer]: waitingForSecondAnswer,
        [ReviewsFilterFieldNameEnum.reviewModerated]: reviewModerated,
        [ReviewsFilterFieldNameEnum.replyPublished]: replyPublished,
        [ReviewsFilterFieldNameEnum.sentiments]: tonality,

        [ReviewsFilterFieldNameEnum.dateFrom]: dateFrom,
        [ReviewsFilterFieldNameEnum.dateTo]: dateTo,
        [ReviewsFilterFieldNameEnum.collectionDateFrom]: collectionDateFrom,
        [ReviewsFilterFieldNameEnum.collectionDateTo]: collectionDateTo,
        [ReviewsFilterFieldNameEnum.replyDateFrom]: replyDateFrom,
        [ReviewsFilterFieldNameEnum.replyDateTo]: replyDateTo,
        [ReviewsFilterFieldNameEnum.agencyRepliers]: agencyRepliers,
    };
}
